import { Injectable, inject } from '@angular/core';

import {
    AppInfoConfig,
    CashierNewOptions,
    ClaimType,
    FrontendHelperService,
    LastKnownProductService,
    NativeAppService,
    NavigationService,
    Page,
    PlatformProductMapConfig,
    ProductHomepagesConfig,
    ProductService,
    TrackingService,
    UserService,
    WINDOW,
    replacePlaceholders,
} from '@frontend/vanilla/core';
import { mapValues } from 'lodash-es';

import { CashierConfig } from './cashier.client-config';
import { DepositType } from './cashier.models';
import { QuickDepositService } from './quick-deposit/quick-deposit.service';

/**
 * @whatItDoes Provides cashier navigation methods.
 *
 * @stable
 */
@Injectable({ providedIn: 'root' })
export class CashierService {
    private readonly user = inject(UserService);
    private readonly cashierConfig = inject(CashierConfig);
    private readonly nativeApp = inject(NativeAppService);
    private readonly page = inject(Page);
    private readonly quickDepositService = inject(QuickDepositService);
    private readonly appInfoConfig = inject(AppInfoConfig);
    private readonly productHomepagesConfig = inject(ProductHomepagesConfig);
    private readonly navigation = inject(NavigationService);
    private readonly frontendHelperService = inject(FrontendHelperService);
    private readonly trackingService = inject(TrackingService);
    private readonly productService = inject(ProductService);
    private readonly lastKnownProductService = inject(LastKnownProductService);
    private readonly platformProductMapConfig = inject(PlatformProductMapConfig);
    readonly #window = inject(WINDOW);

    /** Navigates to cashier entry page. */
    goToCashier(options: CashierNewOptions) {
        options.paymentsType = 'menu';
        this.goToCashierInternal(options);
    }

    goToCashierDeposit(options: CashierNewOptions) {
        options = options || {};
        options.pathTemplate = this.cashierConfig.depositUrlTemplate;
        options.paymentsType = 'deposit';
        if (options.skipQuickDeposit) {
            /** Navigates to cashier standalone deposit page. If user is allowed to quick deposit opens quick deposit overlay. */
            this.goToCashierInternal(options);
        } else {
            this.quickDepositService.isEnabled().subscribe((enabled: boolean) => {
                if (enabled) {
                    this.track(DepositType.Quick, options);
                    this.quickDepositService.open({
                        showKYCVerifiedMessage: options.queryParameters ? options.queryParameters['showKYCVerifiedMessage'] === 'true' : false,
                    });
                } else {
                    this.goToCashierInternal(options);
                }
            });
        }
    }

    /** Navigates to cashier withdrawal page. */
    goToCashierWithdrawal(options: CashierNewOptions) {
        options.pathTemplate = this.cashierConfig.withdrawUrlTemplate;
        options.paymentsType = 'withdrawal';
        this.goToCashierInternal(options);
    }

    /** Navigates to cashier manage my cards page. */
    goToManageMyCards(options: CashierNewOptions) {
        options.pathTemplate = this.cashierConfig.manageMyCardsUrlTemplate;
        options.paymentsType = 'manage-cards';
        this.goToCashierInternal(options);
    }

    /** Navigates to cashier payment history page. */
    goToTransactionHistory(options: CashierNewOptions) {
        options.paymentsType = 'transaction-history';
        this.goToCashierInternal(
            Object.assign(
                <CashierNewOptions>{},
                <CashierNewOptions>{
                    pathTemplate: this.cashierConfig.transactionHistoryUrlTemplate,
                    skipTracking: true,
                },
                options,
            ),
        );
    }

    /** Navigates to payment preferences page. */
    goToPaymentPreferences(options: CashierNewOptions) {
        options = options || {};
        options.pathTemplate = this.cashierConfig.paymentPreferencesUrlTemplate;
        options.paymentsType = 'payment-preferences';
        this.goToCashierInternal(options);
    }

    /** Generates Single domain payments URL */
    generatePaymentsAppUrl(options: CashierNewOptions): string {
        options = this.updateReturnUrl(options);
        let url = options.paymentsAppUrl ?? this.cashierConfig.paymentsAppUrl;

        url = url.replace('{0}', options.paymentsType || 'deposit');
        url = url.replace('{1}', encodeURIComponent(options.returnUrl!));

        options.queryParameters = options.queryParameters ?? {};
        Object.keys(options.queryParameters).forEach((key) => {
            url += `&${key}=${encodeURIComponent(options.queryParameters![key]!)}`;
        });
        return url;
    }

    /** Generates cashier url */
    generateCashierUrl(options: CashierNewOptions): string {
        if (!options.quickDeposit && this.productService.isSingleDomainApp && this.cashierConfig.isPaymentsAppEnabled) {
            return this.generatePaymentsAppUrl(options);
        }

        const defaultOptions = <CashierNewOptions>{
            trackerId: '',
            replaceInHistory: false,
            pathTemplate: this.cashierConfig.urlTemplate,
        };
        options = Object.assign(<CashierNewOptions>{}, defaultOptions, options || <CashierNewOptions>{});

        options = this.updateReturnUrl(options);

        let url = (this.cashierConfig.host ?? '') + options.pathTemplate;

        if (this.cashierConfig.singleSignOnIntegrationType === 'cookie') {
            url = url.replace('sessionKey={0}&', ''); // TODO: update dynacon entries and remove sessionKey={0} once singleSignOnIntegrationType is cookie everywhere. also remove this line after.
        }

        url = url.replace('{0}', this.user.ssoToken || ''); // TODO: remove this line once singleSignOnIntegrationType is cookie everywhere
        url = url.replace('{1}', this.page.lang);
        url = url.replace('{2}', encodeURIComponent(options.returnUrl!));
        url = url.replace('{3}', options.trackerId || '');

        options.queryParameters = options.queryParameters ?? {};

        if (this.user.id) {
            options.queryParameters['userId'] = this.user.id;
        }

        options.queryParameters['channelId'] = this.appInfoConfig.channel;
        options.queryParameters['brandId'] = this.appInfoConfig.brand;
        options.queryParameters['productId'] = this.getProductValue();
        options.queryParameters['frontend'] = this.frontendHelperService.getFrontendDescription();

        if (this.nativeApp.isNativeApp) {
            options.queryParameters['clientAppPath'] = '1';
        } else if (this.nativeApp.isNativeWrapper) {
            options.queryParameters['clientAppPath'] = '2';
        }

        Object.keys(options.queryParameters).forEach((key) => {
            url += `&${key}=${encodeURIComponent(options.queryParameters![key]!)}`;
        });

        return url;
    }

    /* Get platform product value */
    getProductValue(): string {
        if (this.productService.isSingleDomainApp) {
            const platformProductId = this.getPlatformProductId();
            if (platformProductId) {
                return platformProductId;
            }
        }
        return this.appInfoConfig.product;
    }

    private getPlatformProductId(): string {
        const lastKnownProduct = this.lastKnownProductService.get();
        const productName = lastKnownProduct?.name;
        return productName && productName !== 'unknown' ? (this.platformProductMapConfig.platformProductMap[productName] ?? '') : '';
    }

    private updateReturnUrl(options: CashierNewOptions): CashierNewOptions {
        if (!options.returnUrl) {
            options.returnUrl = this.#window.location.href;
        }
        return options;
    }

    handlePartialRegistration(origin: string, returnUrl?: string, options?: CashierNewOptions) {
        if (this.user.claims.get(ClaimType.IsPartiallyRegistered) === 'True') {
            /** Navigates to partial registration if user claim matches. Otherwise, invokes cashier deposit. */
            const depositUrl = this.generateCashierUrl(
                Object.assign(
                    <CashierNewOptions>{
                        pathTemplate: this.cashierConfig.depositUrlTemplate,
                        paymentsType: 'deposit',
                    },
                    options,
                ),
            );
            this.navigation.goTo(`${this.productHomepagesConfig.portal}/mobileportal/partialregistration?rurl=${depositUrl}`);
        } else {
            const newOptions = Object.assign(this.getOptions(origin, returnUrl), options || {});
            this.goToCashierDeposit(newOptions);
        }
    }

    private getOptions(origin: string, returnUrl?: string): CashierNewOptions {
        const trackerId = this.cashierConfig.trackerIds[origin.toLowerCase()] || '';
        returnUrl = returnUrl || this.navigation.location.absUrl();

        return {
            trackerId,
            returnUrl,
        };
    }

    private goToCashierInternal(options: CashierNewOptions) {
        this.track(DepositType.Normal, options);

        const targetWindow = options.cashierTargetWindow ? this.#window[options.cashierTargetWindow] : this.#window;

        if (!targetWindow) {
            return;
        }

        if (this.productService.isSingleDomainApp && this.cashierConfig.isPaymentsAppEnabled) {
            this.goToPaymentsUrl(options);
        } else {
            const url = this.generateCashierUrl(options);
            if (options.replaceInHistory) {
                targetWindow.location.replace(url);
            } else {
                targetWindow.location.href = url;
            }
        }
    }

    private track(type: DepositType, options: CashierNewOptions) {
        if (options.skipTracking || !options.customTracking) {
            return;
        }

        const placeholders = {
            type,
        };

        const event = replacePlaceholders(options.customTracking.eventName, placeholders)!;
        const data = mapValues(options.customTracking.data || {}, (value) => replacePlaceholders(value, placeholders));

        this.trackingService.triggerEvent(event, data);
    }

    private goToPaymentsUrl(options: CashierNewOptions) {
        this.navigation.goTo(this.generateCashierUrl(options));
    }
}
